import {noop} from '@github-ui/noop'
import {testIdProps} from '@github-ui/test-id-props'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {PlusIcon} from '@primer/octicons-react'
import {ActionList, type ActionListItemProps, ActionMenu} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {flushSync} from 'react-dom'

import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import {useHorizontalGroupedBy} from '../features/grouping/hooks/use-horizontal-grouped-by'
import {onSubMenuMultiSelection} from '../helpers/util'
import {ViewerPrivileges} from '../helpers/viewer-privileges'
import {DragDropWithIdsContext} from '../hooks/drag-and-drop/context'
import {useDragWithIds, useDropWithIds} from '../hooks/drag-and-drop/hook'
import {useMemexRootHeight} from '../hooks/use-memex-root-height'
import {useViewOptionsStatsUiKey} from '../hooks/use-view-options-stats-ui-key'
import {useViews} from '../hooks/use-views'
import {useVisibleFields} from '../hooks/use-visible-fields'
import type {ColumnModel} from '../models/column-model'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import {useFindColumnByDatabaseId} from '../state-providers/columns/use-find-column-by-database-id'
import {useIssueFieldsEnabled} from '../state-providers/issue-fields/use-issue-fields-enabled'
import {useAddFieldModal} from '../state-providers/modals/use-add-field-modal'
import styles from './add-column-menu.module.css'
import {AddColumnMenuV2} from './add-column-menu-v2'
import {getColumnIcon} from './column-detail-helpers'
import {normalizeToFilterName} from './filter-bar/helpers/search-filter'

const FieldMenuGroups = {
  NewFieldGroup: 'NewFieldGroup',
  VisibleFieldGroup: 'VisibleFieldGroup',
  HiddenFieldGroup: 'HiddenFieldGroup',
} as const
type FieldMenuGroups = ObjectValues<typeof FieldMenuGroups>

type MenuProps = {
  id?: string
  open: boolean
  setOpen: (open: boolean) => void
  anchorRef: React.RefObject<HTMLElement | null>
}

export const AddColumnMenu = memo<MenuProps>(function AddColumnMenu(props) {
  const issueFieldsEnabled = useIssueFieldsEnabled()

  if (issueFieldsEnabled) {
    return <AddColumnMenuV2 {...props} />
  }
  return <AddColumnMenuLegacy {...props} />
})

export const AddColumnMenuLegacy = memo<MenuProps>(function AddColumnMenuLegacy({id, open, setOpen, anchorRef}) {
  const {clientHeight} = useMemexRootHeight({
    onResize: () => {
      if (open) {
        // eslint-disable-next-line @eslint-react/dom/no-flush-sync
        flushSync(() => {
          setOpen(false)
        })

        setOpen(true)
      }
    },
  })
  return (
    <ActionMenu open={open} anchorRef={anchorRef} onOpenChange={noop}>
      <ActionMenu.Overlay
        style={{
          maxHeight: clientHeight,
        }}
        {...testIdProps('column-visibility-menu')}
        onEscape={() => setOpen(false)}
        onClickOutside={() => setOpen(false)}
        className={styles.ActionMenu_Overlay}
      >
        <Options id={id} key={String(open)} setOpen={setOpen} />
      </ActionMenu.Overlay>
    </ActionMenu>
  )
})

const Options = memo(function Options({id, setOpen}: Pick<MenuProps, 'setOpen' | 'id'>) {
  const {currentView} = useViews()
  const {setShowAddFieldModal, showAddFieldModal} = useAddFieldModal()

  const {allColumns} = useAllColumns()
  const {isFieldVisible, toggleField, visibleFields} = useVisibleFields()

  const {hasWritePermissions} = ViewerPrivileges()
  const statsUiKey = useViewOptionsStatsUiKey()

  const lastToggledColumnIdRef = useRef<string | number | null>(null)
  const actionListRef = useRef<HTMLUListElement>(null)

  // Restore focus to the toggled column after visibleFields changes
  useEffect(() => {
    const toggledColumnId = lastToggledColumnIdRef.current
    if (!toggledColumnId) return

    requestAnimationFrame(() => {
      const toggledElement = actionListRef.current?.querySelector(
        `[data-column-id="${toggledColumnId}"]`,
      ) as HTMLElement

      if (toggledElement && document.contains(toggledElement)) {
        toggledElement.focus()
      }

      lastToggledColumnIdRef.current = null
    })
  }, [visibleFields])

  const onClickNewField = useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      setShowAddFieldModal(true)
      setOpen(false)
      event.stopPropagation()
    },
    [setOpen, setShowAddFieldModal],
  )

  const getItem = useCallback(
    (column: ColumnModel, groupType: FieldMenuGroups, index: number) => {
      const isVisible = isFieldVisible(column)
      const isDisabled = isVisibleFieldDisabled(column)
      const Icon = getColumnIcon(column.dataType)
      return (
        <DragAndDroppableActionListItem
          key={column.id}
          groupId={groupType}
          dragProps={{column, index}}
          disabled={isDisabled}
          data-column-id={column.id}
          onSelect={
            !isDisabled
              ? event => {
                  onSubMenuMultiSelection(event)
                  // the column is currently visible, so the stat is that we are hiding it.
                  if (!currentView) return

                  lastToggledColumnIdRef.current = column.id
                  toggleField(currentView.number, column, undefined, statsUiKey)
                  event.stopPropagation()
                }
              : undefined
          }
          selected={isVisible}
        >
          {/* eslint-disable-next-line primer-react/direct-slot-children */}
          <ActionList.LeadingVisual>
            <Icon />
          </ActionList.LeadingVisual>
          {column.name}
        </DragAndDroppableActionListItem>
      )
    },
    [currentView, isFieldVisible, toggleField, statsUiKey],
  )

  const [visibleFieldItems, hiddenFieldItems] = useMemo(() => {
    const visibleFieldDatabaseIds = new Set(visibleFields.map(col => col.databaseId))
    const hiddenColumns = allColumns.filter(col => !visibleFieldDatabaseIds.has(col.databaseId))
    const visibleItems = []
    const hiddenItems = []

    for (const [index, field] of visibleFields.entries()) {
      visibleItems.push(getItem(field, FieldMenuGroups.VisibleFieldGroup, index))
    }
    for (const [index, field] of hiddenColumns.entries()) {
      hiddenItems.push(getItem(field, FieldMenuGroups.HiddenFieldGroup, index))
    }
    return [visibleItems, hiddenItems]
  }, [allColumns, getItem, visibleFields])

  const addColumnAllowed = useMemo(() => {
    // "New field" option should be enabled if a user has write permissions, the Modal for new field is not shown and it is a table view
    return hasWritePermissions && !showAddFieldModal
  }, [hasWritePermissions, showAddFieldModal])

  return (
    <DragDropWithIdsContext>
      <ActionList ref={actionListRef} id={id} className={styles.ActionList} {...testIdProps('visible-columns-menu')}>
        <ActionList.Group>
          <ActionList.Item
            key={'new-field'}
            disabled={!addColumnAllowed}
            onSelect={e => {
              onSubMenuMultiSelection(e)
              onClickNewField(e)
            }}
            {...testIdProps('new-field-button')}
          >
            <ActionList.LeadingVisual>
              <PlusIcon />
            </ActionList.LeadingVisual>
            New field
          </ActionList.Item>
        </ActionList.Group>
        <ActionList.Divider />
        <ActionList.Group selectionVariant="multiple">
          <ActionList.GroupHeading>Visible fields</ActionList.GroupHeading>
          {visibleFieldItems}
        </ActionList.Group>
        <ActionList.Group selectionVariant="multiple">
          <ActionList.GroupHeading>Hidden fields</ActionList.GroupHeading>
          {hiddenFieldItems}
        </ActionList.Group>
      </ActionList>
    </DragDropWithIdsContext>
  )
})

type DragAndDroppableActionListItemProps = Omit<ActionListItemProps, 'sx'> & {
  dragProps: {column: ColumnModel; index: number}
  groupId: FieldMenuGroups
}

const DragAndDroppableActionListItem = memo(function DragAndDroppableActionListItem({
  dragProps,
  groupId,
  className,
  ...props
}: DragAndDroppableActionListItemProps) {
  const {currentView} = useViews()
  const {groupedByColumn} = useHorizontalGroupedBy()
  const {moveField, hideField, isFieldVisible} = useVisibleFields()
  const {findColumnByDatabaseId} = useFindColumnByDatabaseId()
  const ref = useRef<HTMLDivElement>(null)

  const [isBeingDragged, setIsBeingDragged] = useState(false)
  const [pointerEvents] = useDragClickPointerEvents(isBeingDragged)
  const statsUiKey = useViewOptionsStatsUiKey()

  const drag = useDragWithIds({
    dragID: String(dragProps?.column.databaseId),
    dragType: 'add-column-option',
    dragRef: ref,
    dragIndex: dragProps?.index ?? -1,
    dragOrigin: FieldMenuGroups.VisibleFieldGroup,
    metadata: {},
    disable: !dragProps,
    onDragStart() {
      setIsBeingDragged(true)
    },
    onDragEnd() {
      setIsBeingDragged(false)
    },
  })

  const drop = useDropWithIds({
    dropRef: ref,
    dropID: String(dragProps?.column.databaseId),
    dropType: 'add-column-option',

    onDrop({state: {dragID: dragDropId}}) {
      if (!dragDropId) return
      if (!currentView) return

      const column = findColumnByDatabaseId(Number(dragDropId))
      if (!column) return

      /**
       * If we're dropping on the same column, ignore
       */
      if (dragDropId === String(dragProps?.column.databaseId)) return

      /**
       * If Title or grouped column, don't allow drop
       * otherwise hide field
       */
      if (groupId === FieldMenuGroups.HiddenFieldGroup) {
        if (groupedByColumn === column) return
        if (column.dataType === MemexColumnDataType.Title) return
        hideField(currentView.number, column)
      }

      if (!dragProps) return

      /**
       * Move the dropped field to the position at or after the
       * field it's dropped on
       */
      if (groupId === FieldMenuGroups.VisibleFieldGroup) {
        moveField(currentView.number, column, dragProps.index, statsUiKey)
        return
      }
    },
  })

  return (
    <ActionList.Item
      {...testIdProps(columnItemTestId(isFieldVisible(dragProps.column), dragProps?.column.name))}
      {...drag.props}
      {...drag.handle.props}
      {...drop.props}
      {...props}
      className={clsx(
        className,
        styles.ActionListItem,
        (!dragProps || !isFieldVisible(dragProps.column) || isBeingDragged) && styles.hideSash,
        isBeingDragged && styles.isBeingDragged,
        pointerEvents === 'auto' && styles.pointerEventsAuto,
      )}
    />
  )
})

function useDragClickPointerEvents(isBeingDragged: boolean, timeout = 150) {
  const [pointerEvents, setPointerEvents] = useState<'none' | 'auto'>(() => (isBeingDragged ? 'none' : 'auto'))
  const timeoutValueRef = useTrackingRef(timeout)

  useEffect(
    function handleDragging() {
      if (!isBeingDragged) return

      let mounted = true
      const timer = setTimeout(() => {
        if (mounted) {
          setPointerEvents('none')
        }
      }, timeoutValueRef.current)

      return () => {
        mounted = false
        clearTimeout(timer)
      }
    },
    [isBeingDragged, timeoutValueRef],
  )

  useEffect(
    function handleNotDragging() {
      if (!isBeingDragged) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPointerEvents('auto')
      }
    },
    [isBeingDragged],
  )

  return [pointerEvents, setPointerEvents] as const
}

// Returns true if a field/column should be disabled in the actions menu
function isVisibleFieldDisabled(column: ColumnModel) {
  // Title column is always visible and can't be hidden
  if (column.dataType === MemexColumnDataType.Title) {
    return true
  }

  return false
}

// Returns the test ID of a column item in the actions menu, based on whether it's visible or hidden
function columnItemTestId(isFieldVisible: boolean, columnName: string) {
  const kebabCaseColumnName = normalizeToFilterName(columnName)

  return `${isFieldVisible ? 'visible' : 'hidden'}-column-item-${kebabCaseColumnName}`
}
